import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useNarrow } from '../../../lib/use-narrow';
import { useConversations, getRecentConversationId } from '../../../lib/conversations';
import { useMorningBrief } from '../../../lib/brief';
import { ChatSurface } from '../ChatSurface';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { useConversationRow } from '../../../lib/use-conversation-row';
import { useStartNewConversation } from '../../chat/use-start-new-conversation';
import { useChatOverlayOpen } from '../../../lib/use-chat-overlay';
import { useChatDockWidth } from '../../../lib/use-chat-dock-width';
import { ChatResizeHandle } from './ChatResizeHandle';
import { useMaestroWorking } from '../../../lib/maestro-working';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { ChatFab } from '../ChatFab';
import { MobileChatLauncher } from '../MobileChatLauncher';
import { showsMobileChatLauncher } from '../../../lib/mobile-chat-launcher-routes';
import { useKeyboardInset } from '../use-keyboard-inset';
import { ChatHeader } from './ChatHeader';
import { useVoiceCallSummary } from '../../../lib/voice-call-presence-context';
import { conversationLabel, dateTimeLabel } from './chat-mode';
import { useChatUrlSync } from './use-chat-url';
import {
  CHAT_CLOSE_MS,
  EXPANDED_NARROW,
  EXPANDED_WIDE,
  EXPAND_TRANSITION,
  FULLSCREEN_WIDE,
} from './styles';

export { CHAT_DOCK_WIDTH } from './styles';

/**
 * The one chat. Mounted app-wide in AppFrame and ALWAYS present — its SIZE
 * changes, not its existence. On wide it's a right-docked column (`expanded`) that
 * pushes the content aside, or grown to `fullscreen`; on a phone every open size is
 * full-screen. The inner <ChatSurface> stays mounted across the resize AND across
 * route changes, so a live SSE stream and an in-progress voice call ride straight
 * through — there's only ever one useChat / one voice call.
 */
export function FloatingChat() {
  const narrow = useNarrow();
  const { pathname } = useRouter();
  const { t } = useTranslation();
  const maestro = useMaestroWorking(false);
  const productName = useProductName();
  const { state: conversations } = useConversations(true);
  const { activeConversationId, setActiveConversationId, chatMode, setChatMode, openMaestroChat, retainChatExpanded, briefPeekId } = useActiveConversation();
  const startFreshConversation = useStartNewConversation();
  // A live call takes over the panel with its own header (VoiceCallOverlay's top bar: collapse +
  // settings, per Figma 12835:79206), so the normal chat header is hidden while it's up.
  const voiceCallActive = useVoiceCallSummary()?.timing.surfaceVisible ?? false;

  useChatUrlSync();

  const mode = chatMode;
  const list = conversations.kind === 'ready' ? conversations.conversations : [];
  // The list is one page of conversations, so the chat you are in may not be in it — a chat just created,
  // or one older than the page. Its row is what the header's title and its pin / rename / delete read.
  const active = useConversationRow(
    activeConversationId ?? undefined,
    activeConversationId ? list.find((c) => c.id === activeConversationId) : undefined,
  );
  const isNewChat = !activeConversationId;

  // The morning brief thread isn't in the sidebar's conversation list, so `active`
  // is undefined for it — label it off the brief store, matching the native apps.
  const brief = useMorningBrief();
  // The brief is a transient peek, never the resumable thread. If it ever leaked into
  // the active/stored pointer (an older build persisted it, or a stale sessionStorage
  // entry), home would keep reopening the brief instead of the real last chat. When we
  // see the brief as the active thread WITHOUT an active peek, replace it with the real
  // last chat so resume lands where the user expects.
  useEffect(() => {
    const briefId = brief?.conversationId;
    if (!briefId || activeConversationId !== briefId || briefPeekId === briefId) return;
    let cancelled = false;
    void getRecentConversationId(24 * 60 * 60 * 1000).then((id) => {
      if (!cancelled) setActiveConversationId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [brief, activeConversationId, briefPeekId, setActiveConversationId]);
  const isBriefOpen = !!brief && !!activeConversationId && activeConversationId === brief.conversationId;
  const briefStamp = isBriefOpen ? dateTimeLabel(brief.generatedDate) : undefined;
  const briefTitle = isBriefOpen ? `${t('chat.briefTitle')}${briefStamp ? ` · ${briefStamp}` : ''}` : undefined;
  // Hold the stable "New … chat" label while a just-created thread materializes so
  // the title only ever advances New chat → date → name, never flickering backward.
  const headerTitle = activeConversationId
    ? briefTitle ?? (active ? conversationLabel(active) : t('chat.newChatProduct', { productName }))
    : t('chat.newChatProduct', { productName });

  const open = useChatOverlayOpen();
  const dockWidth = useChatDockWidth();
  // The full-screen mobile chat is `position: fixed; inset: 0; height: 100dvh`. In-app
  // webviews (Instagram/LinkedIn) — like iOS Safari — don't shrink the layout viewport (or
  // 100dvh) when the on-screen keyboard opens, so the bottom-pinned composer sits BEHIND the
  // keyboard. visualViewport does report the gap, so shrink the panel by it to keep the
  // composer above the keyboard (the message ScrollArea is flex:1 and absorbs the change).
  // 0 on browsers that already resize the layout viewport, and irrelevant on wide (no OS keyboard).
  const keyboardInset = useKeyboardInset();
  const [dragging, setDragging] = useState(false);
  // The per-chat Files/Media panel: opened from the header, rendered by ChatSurface
  // (which owns the messages it lists). Only offered once a real conversation exists.
  const [filesOpen, setFilesOpen] = useState(false);
  // Snapshot the fullscreen state so the close animation reads the right geometry
  // after chatMode has flipped to `bubble`.
  const liveFullscreen = !narrow && mode === 'fullscreen';
  const [closing, setClosing] = useState(false);
  const lastFullscreenRef = useRef(liveFullscreen);
  if (open) lastFullscreenRef.current = liveFullscreen;
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open) {
      setClosing(false);
      return;
    }
    if (wasOpen) {
      setClosing(true);
      const timer = window.setTimeout(() => setClosing(false), CHAT_CLOSE_MS);
      return () => window.clearTimeout(timer);
    }
  }, [open]);

  const fullscreen = open ? liveFullscreen : lastFullscreenRef.current;

  // Once the chat has been opened it stays MOUNTED for the rest of the session —
  // collapsing to the bubble only hides it. Unmounting used to tear down
  // <ChatSurface> and with it the single useChat: an in-flight turn's SSE was
  // aborted mid-thought, and a thread whose id hadn't come back from the server
  // yet was lost, so reopening dropped the user into a brand-new conversation.
  // Since a navigation can collapse the chat (narrow), that made "click a nav
  // item" silently kill the turn you were waiting on.
  const everOpenedRef = useRef(false);
  if (open) everOpenedRef.current = true;
  const resting = !open && !closing;
  // Nothing to preserve before the first open — stay unmounted so the chat costs
  // nothing (no conversation fetch, no duplexer) for a user who never opens it.
  const mounted = !resting || everOpenedRef.current;

  // Side dock only: a user-resized width (default CHAT_DOCK_WIDTH) that AppFrame's
  // --chat-w mirrors so the content pane reflows with it. Fullscreen/narrow ignore it.
  const docked = !narrow && !fullscreen;
  const geometry = narrow ? EXPANDED_NARROW : fullscreen ? FULLSCREEN_WIDE : { ...EXPANDED_WIDE, width: dockWidth };
  // Same div whether side dock or fullscreen — only the geometry/chrome differ, so
  // the transition animates the grow/shrink in place (no cross-fade, no remount).
  const panelStyle: CSSProperties = {
    ...geometry,
    // The personal-sale banner is a top band that must show ABOVE the chat, not be covered by this z-50
    // sheet. Two DIFFERENT bands, each 0px when absent:
    //  - `--personal-sale-banner-h`: the IN-CONTENT banner (mobile any screen, desktop non-/packages). It
    //    sits inside the content region, so only a sheet that covers that region clears it — NARROW (fixed
    //    inset:0) and wide FULLSCREEN. A desktop DOCK is beside the content, so the banner shows next to it.
    //  - `--personal-sale-topbar-h`: the desktop /packages full-viewport blue strip above <main>. It spans
    //    the whole top, so EVERY non-narrow sheet — dock AND fullscreen — must clear it or it covers the X.
    //
    // Narrow ALSO drops below the minimized-call strip (--voice-island-h, set by VoiceCallStrip) and off
    // the on-screen keyboard (keyboardInset); all subtract from the height so the sheet still reaches the
    // viewport bottom. `top` is in EXPAND_TRANSITION so the dock↔fullscreen shift eases rather than jumps.
    ...(narrow
      ? {
          top: 'calc(var(--personal-sale-banner-h, 0px) + var(--voice-island-h, 0px))',
          height: `calc(100dvh - var(--personal-sale-banner-h, 0px) - var(--voice-island-h, 0px) - ${keyboardInset}px)`,
        }
      : fullscreen
        ? {
            top: 'calc(var(--personal-sale-banner-h, 0px) + var(--personal-sale-topbar-h, 0px))',
            height: 'calc(100dvh - var(--personal-sale-banner-h, 0px) - var(--personal-sale-topbar-h, 0px))',
          }
        : {
            // Desktop DOCK: only the /packages topbar strip needs clearing (--personal-sale-banner-h is 0
            // on /packages, and off /packages the in-content banner shows beside the dock, so this stays
            // at top:0 there — the var is absent → 0px, no change to the existing docked behaviour).
            top: 'var(--personal-sale-topbar-h, 0px)',
            height: 'calc(100dvh - var(--personal-sale-topbar-h, 0px))',
          }),
    // No eased width while dragging the resize handle, or the edge lags the cursor.
    transition: dragging ? 'none' : EXPAND_TRANSITION,
    ...(closing && !open
      ? {
          animation: narrow
            ? `orion-sheet-out ${CHAT_CLOSE_MS}ms cubic-bezier(0.32, 0.72, 0, 1) forwards`
            : `orion-chat-slide-out ${CHAT_CLOSE_MS}ms cubic-bezier(0.32, 0.72, 0, 1) forwards`,
          pointerEvents: 'none',
        }
      : null),
    // At rest: laid out (so the scroll position and message metrics survive) but
    // invisible, untabbable and out of the a11y tree — visibility:hidden does all
    // three. `animation: none` matters too: the panel is no longer remounted on
    // open, so clearing the name here is what lets the entrance animation replay
    // when the geometry's animation is set again.
    ...(resting ? { visibility: 'hidden', pointerEvents: 'none', animation: 'none' } : null),
  };

  return (
    <>
      {resting &&
        // Narrow: the FAB opens an input-only composer first, which hands off on send. It appears only
        // on the three screens the native apps float it on (`showsMobileChatLauncher`) — on a phone it
        // floats OVER the content, so anywhere else it is just something covering the last row. Wide is
        // NOT gated: there it sits beside the content and is the only door into the dock.
        (narrow ? (
          showsMobileChatLauncher(pathname) ? <MobileChatLauncher /> : null
        ) : (
          // Wide: resume the active thread if there is one, else start a fresh Maestro chat.
          <ChatFab
            onClick={() => (activeConversationId ? setChatMode('expanded') : openMaestroChat())}
            label={t('chat.openProduct', { productName })}
            // This branch only renders while the chat is CLOSED, which is exactly when the indicator
            // belongs: with the chat open the transcript is already saying it.
            working={maestro.working}
            workingLabel={t('agentsIsland.maestroWorking')}
          />
        ))}
      {mounted && (
        <div style={panelStyle}>
          {docked && open && (
            <ChatResizeHandle width={dockWidth} label={t('chat.resizeHandle')} onDraggingChange={setDragging} />
          )}
          {!voiceCallActive && (
          <ChatHeader
            narrow={narrow}
            fullscreen={fullscreen}
            isNewChat={isNewChat}
            headerTitle={headerTitle}
            conversation={active}
            onNewConversation={() => {
              // Keep the open chat expanded across the switch — clearing the thread makes
              // it an empty landing, which on the home route would otherwise self-collapse
              // to the bubble (MessagesPanel's "no second home" rule).
              retainChatExpanded();
              void startFreshConversation();
            }}
            onCompose={() => openMaestroChat()}
            onExpand={() => setChatMode('fullscreen')}
            onShrink={() => setChatMode('expanded')}
            onMinimize={() => setChatMode('bubble')}
            onOpenFiles={isNewChat ? undefined : () => setFilesOpen(true)}
          />
          )}
          {/* The chat surface fills the rest; minHeight:0 lets the inner scroll area
              shrink instead of overflowing the panel. */}
          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <ChatSurface
              variant="floating"
              expanded
              sideDocked={!narrow && !fullscreen}
              filesOpen={filesOpen && !isNewChat}
              onCloseFiles={() => setFilesOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
